import {
  type Column,
  flexRender,
  type Row,
  type RowData,
  type VisibilityState,
} from "@tanstack/react-table";
import {
  type CSSProperties,
  type MouseEvent,
  memo,
  type ReactElement,
} from "react";

import { cn } from "../../utils";
import { TableCell, TableRow } from "../table";

export const getPinningStyles = <TData extends RowData>(
  column: Column<TData>,
  // From `distributeColumnWidths` — absent until the table has been measured, or when the table
  // sizes its columns by percentage instead.
  width?: number,
): CSSProperties => {
  const isPinned = column.getIsPinned();
  // A numeric `stretch` is a percentage of the table, which lets several columns be fluid at once
  // (percentages resolve against the table's own width). No `minWidth`: the column keeps shrinking
  // so the table always fits, and the cell's content handles the overflow (wrap, or
  // `TruncatedText`'s ellipsis + tooltip).
  const stretch = column.columnDef.meta?.stretch;
  const stretchPercent =
    !isPinned && typeof stretch === "number" ? stretch : undefined;

  return {
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    position: isPinned ? "sticky" : "relative",
    width:
      stretchPercent !== undefined
        ? `${stretchPercent}%`
        : (width ?? column.getSize()),
    zIndex: isPinned ? 1 : 0,
  };
};

const isModifiedClick = (event: MouseEvent<HTMLElement>) =>
  event.button !== 0 ||
  event.ctrlKey ||
  event.metaKey ||
  event.shiftKey ||
  event.altKey;

const openInNewTab = (href: string) => window.open(href, "_blank", "noopener");

const NESTED_INTERACTIVE = "a,button,input,label,[role=menuitem]";

const isNestedInteractive = (target: EventTarget, row: HTMLElement) => {
  if (!(target instanceof Element)) return false;
  const match = target.closest(NESTED_INTERACTIVE);
  return match !== null && row.contains(match);
};

interface IMemoizedTableRowProps<TData extends RowData> {
  row: Row<TData>;
  enableRowSelection: boolean;
  getRowClassName?: (row: TData) => string | undefined;
  onRowClick?: (row: TData) => void;
  getRowHref?: (row: TData) => string | undefined;
  isExpanded?: boolean;
  isSelected?: boolean;
  // Unused below — only here so React.memo's shallow prop compare invalidates when
  // visibility changes. `row`'s own identity is stable across a visibility-only
  // re-render (same core row model), so getVisibleCells() would otherwise stay stale.
  columnVisibility?: VisibilityState;
  columnWidths?: Record<string, number> | null;
}

export const MemoizedTableRow = memo(
  <TData extends RowData>({
    row,
    enableRowSelection,
    getRowClassName,
    onRowClick,
    getRowHref,
    columnWidths,
  }: IMemoizedTableRowProps<TData>) => {
    const rowClassName = getRowClassName?.(row.original);
    const href = getRowHref?.(row.original);
    return (
      <TableRow
        data-state={enableRowSelection && row.getIsSelected() && "selected"}
        className={cn(
          "group bg-white",
          onRowClick &&
            "cursor-pointer focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[-2px]",
          rowClassName,
        )}
        onClick={
          onRowClick || href
            ? (event) => {
                if (event.defaultPrevented) return;
                // A control inside the row owns its own click; the row must not also navigate.
                if (isNestedInteractive(event.target, event.currentTarget))
                  return;
                if (isModifiedClick(event)) {
                  // Only the first cell carries a real anchor, so a ctrl/cmd-click anywhere else on the
                  // row has to open the target itself.
                  if (
                    href &&
                    event.button === 0 &&
                    (event.ctrlKey || event.metaKey)
                  )
                    openInNewTab(href);
                  return;
                }
                onRowClick?.(row.original);
              }
            : undefined
        }
        onAuxClick={
          href
            ? (event) => {
                if (event.button !== 1) return;
                if (isNestedInteractive(event.target, event.currentTarget))
                  return;
                event.preventDefault();
                openInNewTab(href);
              }
            : undefined
        }
        // A clickable row is often the ONLY route to a detail page (the lists that navigate this
        // way have no row action button), so it has to be reachable without a mouse. Space is
        // preventDefault-ed because it would otherwise scroll the page.
        // With an href the first cell's anchor is the keyboard stop instead.
        tabIndex={onRowClick && !href ? 0 : undefined}
        role={onRowClick && !href ? "button" : undefined}
        onKeyDown={
          onRowClick && !href
            ? (event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                // Let a control inside the row (a switch, a menu) handle its own keys.
                if (event.target !== event.currentTarget) return;
                event.preventDefault();
                onRowClick(row.original);
              }
            : undefined
        }
      >
        {row.getVisibleCells().map((cell, index) => {
          const { column } = cell;
          const isPinned = column.getIsPinned();
          const dataLastCol: "left" | "right" | undefined =
            isPinned === "left" && column.getIsLastColumn("left")
              ? "left"
              : isPinned === "right" && column.getIsFirstColumn("right")
                ? "right"
                : undefined;

          return (
            <TableCell
              key={cell.id}
              className={cn(
                // transparent so row hover:bg-gray-100/50 shows through; pinned need solid bg for sticky
                // "bg-transparent",
                isPinned && "bg-white",
                rowClassName,
                [
                  "[&[data-pinned=left][data-last-col=left]]:border-r",
                  "[&[data-pinned=right][data-last-col=right]]:border-l",

                  // shadow looks weird when pinned
                  // "[&[data-pinned=right][data-last-col=right]]:shadow-lg",
                  // "[&[data-pinned=left][data-last-col=left]]:shadow-lg",

                  "[&[data-pinned][data-last-col]]:border-gray-200",
                ],
                column.columnDef.meta?.cellClassName,
                // "backdrop-blur-md",
              )}
              style={getPinningStyles(column, columnWidths?.[column.id])}
              data-pinned={isPinned || undefined}
              data-last-col={dataLastCol}
            >
              {href && index === 0 ? (
                <a
                  href={href}
                  className="block text-inherit no-underline"
                  onClick={(event) => {
                    if (isModifiedClick(event) || !onRowClick) return;
                    // An interactive control inside the first cell owns its own click.
                    if (
                      event.target instanceof Element &&
                      event.target.closest(NESTED_INTERACTIVE) !==
                        event.currentTarget
                    )
                      return;
                    event.preventDefault();
                    onRowClick(row.original);
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </a>
              ) : (
                flexRender(cell.column.columnDef.cell, cell.getContext())
              )}
            </TableCell>
          );
        })}
      </TableRow>
    );
  },
) as <TData extends RowData>(
  props: IMemoizedTableRowProps<TData>,
) => ReactElement | null;
